import React, { useMemo } from "react";
import { Printer, Check, MessageSquare, PenLine, Mail, Phone, MapPin, ArrowRight, Clock, ShieldCheck, Sparkles } from "lucide-react";
import { fmtNOK, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MediabilityLogo } from "@/components/Logo";
import { CompanyLogo, ProductImage, UserAvatar, BrandMark } from "@/components/common";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ helpers */

export const NAV = [
  { id: "summary", label: "Summary" },
  { id: "solution", label: "Solution" },
  { id: "investment", label: "Investment" },
  { id: "timeline", label: "Timeline" },
  { id: "terms", label: "Terms" },
  { id: "acceptance", label: "Accept" },
];

export function unitPrice(it) {
  return (it.sales_price || 0) * (1 - (it.discount_pct || 0) / 100);
}

export function groupTotal(g) {
  return (g.items || []).reduce((a, it) => a + unitPrice(it) * (it.qty || 0), 0);
}

export function isServiceGroup(g) {
  const items = g.items || [];
  return items.length > 0 && items.every((it) => it.is_service);
}

function Para({ text, className }) {
  if (!text) return null;
  return String(text)
    .split(/\n{2,}/)
    .map((p, i) => (
      <p key={i} className={cn(i ? "mt-4" : "", className)}>
        {p}
      </p>
    ));
}

function Bullets({ items, testId, className }) {
  if (!items?.length) return <p className="text-muted-foreground">None.</p>;
  return (
    <ul className={cn("space-y-2", className)} data-testid={testId}>
      {items.map((x, i) => (
        <li key={i} className="flex gap-3 text-[14.5px] leading-6">
          <span className="mt-[11px] h-1 w-1 shrink-0 rounded-full bg-primary" />
          <span>{x}</span>
        </li>
      ))}
    </ul>
  );
}

/** Editorial chapter: number + title on the left, content on the right. */
function Chapter({ id, n, title, lede, children, className, testId }) {
  return (
    <section id={id} className={cn("proposal-section scroll-mt-24 border-t border-black/[0.08] pt-10 @2xl:pt-12", className)} data-testid={testId || `public-quote-section-${n}`}>
      <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-[220px_1fr] @4xl:gap-14">
        <div className="@4xl:sticky @4xl:top-24 @4xl:self-start">
          <div className="font-mono text-[11px] tracking-[0.12em] text-primary">{String(n).padStart(2, "0")}</div>
          <h2 className="font-display mt-2 text-[22px] font-semibold leading-[1.15] tracking-[-0.02em] @2xl:text-[26px]">{title}</h2>
          {lede ? <p className="mt-3 text-[13.5px] leading-6 text-muted-foreground">{lede}</p> : null}
        </div>
        <div className="min-w-0">{children}</div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ hero mosaic */

function Mosaic({ items }) {
  const [a, b, c] = items;
  if (!a) return null;
  return (
    <div className="grid aspect-[5/4] grid-cols-3 grid-rows-2 gap-3 @2xl:gap-4 @4xl:aspect-[6/5]" data-testid="public-quote-mosaic">
      <MosaicTile item={a} className="col-span-2 row-span-2" big />
      {b ? <MosaicTile item={b} /> : <span className="rounded-[18px] border border-black/[0.06] bg-[hsl(40_12%_96%)]" />}
      {c ? <MosaicTile item={c} /> : <span className="rounded-[18px] border border-black/[0.06] bg-[hsl(40_12%_96%)]" />}
    </div>
  );
}

function MosaicTile({ item, className, big }) {
  return (
    <figure className={cn("group relative overflow-hidden rounded-[18px] border border-black/[0.06] bg-[hsl(40_12%_96%)]", className)}>
      <ProductImage item={item} size="absolute inset-0 h-full w-full rounded-none border-0" fit="auto" imgClassName="transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
      <figcaption className="pointer-events-none absolute bottom-2.5 left-2.5 max-w-[calc(100%-20px)] rounded-full bg-[hsl(0_0%_8%/0.78)] px-3 py-1.5 text-white backdrop-blur-md @2xl:bottom-3 @2xl:left-3">
        <span className={cn("block truncate font-medium leading-tight", big ? "text-[13px] @2xl:text-[14px]" : "text-[11.5px] @2xl:text-[12px]")}>
          {item.qty > 1 ? `${item.qty} × ` : ""}
          {item.name}
        </span>
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ solution chapter */

function SolutionGroup({ g, index }) {
  const items = g.items || [];
  const services = isServiceGroup(g);
  const hero = items.find((it) => it.image_url);
  const totalQty = items.reduce((a, it) => a + (it.qty || 0), 0);
  return (
    <div className="proposal-section grid grid-cols-1 gap-5 py-7 first:pt-0 @4xl:grid-cols-[200px_1fr] @4xl:gap-8" data-testid="public-quote-solution-group">
      <div>
        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          <span className="font-mono text-primary">{String(index + 1).padStart(2, "0")}</span>
          {services ? `${totalQty} hours` : `${totalQty} ${totalQty === 1 ? "unit" : "units"}`}
        </div>
        <h3 className="font-display mt-1.5 text-[18px] font-semibold leading-tight tracking-[-0.015em]">{g.name}</h3>
        {hero && !services ? (
          <div className="mt-4 hidden @4xl:block">
            <ProductImage item={hero} ratio="wide" className="rounded-[14px]" />
          </div>
        ) : null}
      </div>
      <ul className="divide-y divide-black/[0.06] rounded-[16px] border border-black/[0.07] bg-white">
        {items.map((it) => (
          <li key={it.id} className="flex items-start gap-4 px-4 py-3.5 @2xl:px-5" data-testid="public-quote-solution-item">
            {services ? (
              <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-[hsl(40_12%_96%)] text-muted-foreground">
                <Clock className="h-4 w-4" />
              </span>
            ) : (
              <ProductImage item={it} size="h-12 w-16" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-[14.5px] font-medium leading-snug">{it.name}</span>
                {!services && it.manufacturer ? (
                  <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <BrandMark name={it.manufacturer} domain={it.manufacturer_domain} size="sm" className="h-4 w-4 rounded-[4px] border-0 p-0" />
                    {it.manufacturer}
                  </span>
                ) : null}
              </div>
              {it.reason || it.description ? <p className="mt-0.5 text-[13px] leading-5 text-muted-foreground">{it.reason || it.description}</p> : null}
            </div>
            <span className="shrink-0 pt-0.5 text-right font-mono text-[12.5px] tabular text-muted-foreground">
              {it.qty}
              {it.unit && it.unit !== "pcs" ? ` ${it.unit}` : services ? " h" : " ×"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* a scene image – a photograph of the kind of room/production this proposal is about (optional, from quote.images) */
function Scene({ img, className, ratio = "aspect-[16/9]", testId }) {
  if (!img?.url) return null;
  return (
    <figure className={cn("relative overflow-hidden rounded-[18px] bg-black/[0.04]", ratio, className)} data-testid={testId}>
      <img src={img.url} alt={img.alt || img.caption || ""} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      {img.caption ? (
        <figcaption className="absolute bottom-3 left-3 rounded-full bg-black/55 px-3 py-1.5 text-[11.5px] font-medium text-white backdrop-blur-md">{img.caption}</figcaption>
      ) : null}
    </figure>
  );
}

/* ------------------------------------------------------------------ document */

/**
 * The proposal itself - shared by the public customer page and the AI Workspace preview.
 * `response` (optional): { mode, setMode, name, setName, msg, setMsg, busy, respond, done } enables the accept/respond UI.
 * Without it the document renders read-only (preview).
 */
export function ProposalDocument({ data, response, className }) {
  const { mode, setMode, name = "", setName, msg = "", setMsg, busy, respond, done } = response || {};
  const interactive = !!response;
  const q = data?.quote;
  const s = q?.sections || {};
  const t = q?.totals || {};
  const groups = useMemo(() => (q?.items_snapshot || []).filter((g) => (g.items || []).length), [q]);
  const productGroups = useMemo(() => groups.filter((g) => !isServiceGroup(g)), [groups]);
  const serviceGroups = useMemo(() => groups.filter((g) => isServiceGroup(g)), [groups]);
  const featured = useMemo(() => {
    const all = groups.flatMap((g) => g.items || []).filter((it) => it.image_url && !it.is_service);
    const seen = new Set();
    const uniq = all.filter((it) => (seen.has(it.product_id) ? false : (seen.add(it.product_id), true)));
    return uniq.sort((a, b) => unitPrice(b) * b.qty - unitPrice(a) * a.qty).slice(0, 3);
  }, [groups]);
  // eslint-disable-next-line no-unused-vars
  const contacts = (data?.contacts || []).filter((c) => c.is_decision_maker).concat((data?.contacts || []).filter((c) => !c.is_decision_maker)).slice(0, 2);

  const ref = `Q-${new Date(q.created_at).getFullYear()}-${(q.token || "").slice(0, 4).toUpperCase()}-v${q.version}`;
  const accepted = q.customer_accepted_at || done === "accept";
  const company = data.company || {};
  const account = data.account || {};
  const owner = data.owner || {};
  const entity = (company.legal_entities || []).find((e) => e.country === (account.country || "Norway")) || (company.legal_entities || [])[0] || {};
  const dateStr = fmtDate(q.sent_at || q.created_at, { year: true });
  const benefits = (s.key_benefits || []).slice(0, 4);
  const images = q.images || {};
  const cinematic = !!images.cover?.url;

  return (
    <article className={cn("print-page @container", className)} data-testid="public-quote-document">
          {/* ------------------------------------------------ cover */}
          <header className="relative" data-testid="public-quote-cover">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
                <span className="font-mono text-[12px] tracking-wide" data-testid="public-quote-ref">{ref}</span>
                <span className="h-1 w-1 rounded-full bg-black/20" />
                <span>{dateStr}</span>
              </div>
              <div className="flex items-center gap-3 rounded-full border border-black/[0.07] bg-white py-1 pl-1 pr-4" data-testid="public-quote-prepared-for">
                <CompanyLogo name={account.name} domain={account.website} size="sm" className="rounded-full" />
                <span className="text-[13px]">
                  <span className="text-muted-foreground">Prepared for </span>
                  <span className="font-semibold" data-testid="public-quote-account">{account.name}</span>
                </span>
              </div>
            </div>

            {cinematic ? (
              <>
                {/* the room this is about, with the title on it */}
                <div className="relative mt-8 overflow-hidden rounded-[22px] bg-[hsl(var(--ink))] text-white @2xl:mt-10 @2xl:rounded-[26px]" data-testid="public-quote-cover-image">
                  <div className="relative aspect-[4/5] @2xl:aspect-[16/10] @4xl:aspect-[21/10]">
                    <img src={images.cover.url} alt={images.cover.alt || ""} className="absolute inset-0 h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
                    <div className="absolute inset-x-0 bottom-0 flex flex-col gap-6 p-6 @2xl:flex-row @2xl:items-end @2xl:justify-between @2xl:p-9 @4xl:p-11">
                      <div className="min-w-0 max-w-[640px]">
                        <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                          <span className="h-px w-6 bg-primary" /> Proposal
                        </div>
                        <h1 className="font-display mt-4 text-[32px] font-semibold leading-[1.02] tracking-[-0.035em] @2xl:text-[44px] @4xl:text-[54px] @5xl:text-[60px]" data-testid="public-quote-title">
                          {q.title}
                        </h1>
                        {data.opportunity?.name ? <p className="mt-3 text-[15px] text-white/75 @2xl:text-[17px]">{data.opportunity.name}</p> : null}
                      </div>
                      {featured.length > 0 ? (
                        <ul className="flex shrink-0 items-end gap-2" data-testid="public-quote-cover-products">
                          {featured.map((it) => (
                            <li key={it.id} className="relative h-[68px] w-[68px] overflow-hidden rounded-[14px] border border-white/15 bg-white @2xl:h-[84px] @2xl:w-[84px]" title={`${it.qty} × ${it.name}`}>
                              <img src={it.image_url} alt={it.name} className="h-full w-full object-contain p-1.5" />
                              <span className="absolute bottom-1 right-1 rounded-full bg-black/70 px-1.5 py-0.5 font-mono text-[10px] leading-none text-white">{it.qty}×</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* the four facts in a row */}
                <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-6 border-y border-black/[0.07] py-6 @2xl:grid-cols-4 @2xl:gap-x-8" data-testid="public-quote-facts">
                  <div>
                    <dt className="eyebrow">Investment</dt>
                    <dd className="font-display mt-2 whitespace-nowrap text-[22px] font-semibold tabular leading-none tracking-[-0.03em] @4xl:text-[26px]" data-testid="public-quote-total">
                      {fmtNOK(t.total_sales)} <span className="text-[13px] font-medium text-muted-foreground">NOK</span>
                    </dd>
                    <dd className="mt-1.5 text-[12px] text-muted-foreground">excl. VAT</dd>
                  </div>
                  <div>
                    <dt className="eyebrow">Valid until</dt>
                    <dd className="font-display mt-2 whitespace-nowrap text-[22px] font-semibold tabular leading-none tracking-[-0.03em] @4xl:text-[26px]">{fmtDate(q.valid_until, { year: true })}</dd>
                    <dd className="mt-1.5 text-[12px] text-muted-foreground">version {q.version}</dd>
                  </div>
                  <div>
                    <dt className="eyebrow">Delivery</dt>
                    <dd className="font-display mt-2 whitespace-nowrap text-[22px] font-semibold tabular leading-none tracking-[-0.03em] @4xl:text-[26px]">{t.max_lead_time_weeks ? `${t.max_lead_time_weeks} wks` : "–"}</dd>
                    <dd className="mt-1.5 text-[12px] text-muted-foreground">longest lead time</dd>
                  </div>
                  <div>
                    <dt className="eyebrow">Your contact</dt>
                    <dd className="mt-2 flex items-center gap-2.5">
                      <UserAvatar name={owner.name} initials={owner.initials} color={owner.color} src={owner.avatar_url} size="md" className="h-9 w-9" />
                      <span className="min-w-0 leading-tight">
                        <span className="block truncate text-[13.5px] font-semibold" data-testid="public-quote-owner">{owner.name}</span>
                        <span className="block truncate text-[11.5px] text-muted-foreground">{owner.title || "Mediability"}</span>
                      </span>
                    </dd>
                  </div>
                </dl>
              </>
            ) : (
            <div className="mt-12 grid grid-cols-1 gap-10 @2xl:mt-16 @4xl:grid-cols-[1.05fr_1fr] @4xl:items-end @4xl:gap-16">
                <div>
                  <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
                    <span className="h-px w-6 bg-primary" /> Proposal
                  </div>
                  <h1 className="font-display mt-5 text-[34px] font-semibold leading-[1.02] tracking-[-0.035em] @2xl:text-[46px] @4xl:text-[54px] @5xl:text-[60px]" data-testid="public-quote-title">
                    {q.title}
                  </h1>
                  <p className="mt-5 max-w-xl text-[16px] leading-7 text-muted-foreground @2xl:text-[18px]">{data.opportunity?.name}</p>

                  <dl className="mt-10 grid grid-cols-2 gap-x-8 gap-y-7 @2xl:grid-cols-4 @4xl:grid-cols-2">
                    <div>
                      <dt className="eyebrow">Investment</dt>
                      <dd className="font-display mt-2 whitespace-nowrap text-[22px] font-semibold tabular leading-none tracking-[-0.03em]" data-testid="public-quote-total">
                        {fmtNOK(t.total_sales)} <span className="text-[13px] font-medium text-muted-foreground">NOK</span>
                      </dd>
                      <dd className="mt-1.5 text-[12px] text-muted-foreground">excl. VAT</dd>
                    </div>
                    <div>
                      <dt className="eyebrow">Valid until</dt>
                      <dd className="font-display mt-2 whitespace-nowrap text-[22px] font-semibold tabular leading-none tracking-[-0.03em]">{fmtDate(q.valid_until, { year: true })}</dd>
                      <dd className="mt-1.5 text-[12px] text-muted-foreground">version {q.version}</dd>
                    </div>
                    <div>
                      <dt className="eyebrow">Delivery</dt>
                      <dd className="font-display mt-2 whitespace-nowrap text-[22px] font-semibold tabular leading-none tracking-[-0.03em]">{t.max_lead_time_weeks ? `${t.max_lead_time_weeks} wks` : "–"}</dd>
                      <dd className="mt-1.5 text-[12px] text-muted-foreground">longest lead time</dd>
                    </div>
                    <div>
                      <dt className="eyebrow">Your contact</dt>
                      <dd className="mt-2 flex items-center gap-2.5">
                        <UserAvatar name={owner.name} initials={owner.initials} color={owner.color} src={owner.avatar_url} size="md" className="h-8 w-8" />
                        <span className="min-w-0 leading-tight">
                          <span className="block truncate text-[13.5px] font-semibold" data-testid="public-quote-owner">{owner.name}</span>
                          <span className="block truncate text-[11.5px] text-muted-foreground">{owner.title || "Mediability"}</span>
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>

                {featured.length > 0 ? (
                  <div className="@4xl:pb-1">
                    <Mosaic items={featured} />
                  </div>
                ) : (
                  <div className="relative hidden overflow-hidden rounded-[18px] border border-black/[0.06] bg-[hsl(var(--ink))] p-8 text-white @4xl:block" data-testid="public-quote-cover-panel">
                    <span className="pointer-events-none absolute -right-12 -top-16 h-56 w-56 rounded-full bg-[hsl(10_76%_61%/0.35)] blur-3xl" />
                    <div className="relative">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-white/60">Scope</div>
                      <div className="mt-3 space-y-2">
                        {productGroups.slice(0, 6).map((g) => (
                          <div key={g.id} className="flex items-baseline justify-between gap-4 border-t border-white/10 pt-2 text-[14px]">
                            <span>{g.name}</span>
                            <span className="font-mono text-[12px] text-white/60">{(g.items || []).reduce((a, it) => a + (it.qty || 0), 0)} units</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </header>

          {/* ------------------------------------------------ at a glance */}
          {benefits.length > 0 ? (
            <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-[18px] border border-black/[0.07] bg-black/[0.06] @2xl:grid-cols-2 @4xl:grid-cols-4" data-testid="public-quote-benefits">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white px-5 py-5">
                  <div className="font-mono text-[11px] tracking-[0.12em] text-primary">{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-2 text-[14.5px] font-medium leading-snug">{b}</div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="mt-16 space-y-16 @2xl:mt-20 @2xl:space-y-20">
            {/* ------------------------------------------------ summary */}
            <Chapter id="summary" n={1} title="Executive summary" lede={`Why we recommend this for ${account.name}.`}>
              <div className={cn("grid grid-cols-1 gap-8", images.summary?.url && "@4xl:grid-cols-[minmax(0,1fr)_280px] @4xl:items-start")}>
                <div className="max-w-[640px] text-[16px] leading-[1.75] text-foreground/90 @2xl:text-[17px]">
                  <Para text={s.executive_summary} className="first:text-[18px] first:leading-[1.65] @2xl:first:text-[20px]" />
                </div>
                <Scene img={images.summary} ratio="aspect-[4/5]" className="hidden @4xl:block" testId="public-quote-scene-summary" />
              </div>
              {s.our_understanding ? (
                <div className="mt-10 max-w-[640px] border-l-2 border-primary/60 pl-5 text-[15px] leading-7 text-foreground/80">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Our understanding</div>
                  <Para text={s.our_understanding} />
                </div>
              ) : null}
            </Chapter>

            {/* ------------------------------------------------ solution */}
            <Chapter id="solution" n={2} title="The solution" lede={s.proposed_solution ? undefined : "What we deliver and why each part was chosen."}>
              {s.proposed_solution ? (
                <div className="max-w-[640px] text-[15.5px] leading-7 text-foreground/90">
                  <Para text={s.proposed_solution} />
                </div>
              ) : null}
              {s.system_design ? (
                <div className="mt-8 flex gap-4 rounded-[16px] border border-black/[0.07] bg-white p-5" data-testid="public-quote-system-design">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[hsl(var(--ink))] text-white">
                    <Sparkles className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">System design</div>
                    <p className="mt-1.5 text-[14.5px] leading-6">{s.system_design}</p>
                  </div>
                </div>
              ) : null}
              <Scene img={images.solution} ratio="aspect-[16/9] @2xl:aspect-[21/9]" className="mt-8" testId="public-quote-scene-solution" />
              <div className="mt-10 divide-y divide-black/[0.06]" data-testid="public-quote-items">
                {productGroups.map((g, i) => (
                  <SolutionGroup key={g.id} g={g} index={i} />
                ))}
                {serviceGroups.map((g, i) => (
                  <SolutionGroup key={g.id} g={g} index={productGroups.length + i} />
                ))}
              </div>
              {(s.scope_hardware || s.scope_software || s.scope_services) && (
                <div className="mt-8 grid grid-cols-1 gap-6 text-[13.5px] leading-6 text-muted-foreground @2xl:grid-cols-3">
                  {s.scope_hardware ? <div><div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em]">Hardware</div>{s.scope_hardware}</div> : null}
                  {s.scope_software ? <div><div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em]">Software</div>{s.scope_software}</div> : null}
                  {s.scope_services ? <div><div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em]">Services</div>{s.scope_services}</div> : null}
                </div>
              )}
            </Chapter>

            {/* ------------------------------------------------ investment */}
            <Chapter id="investment" n={3} title="Investment" lede={`All prices in NOK excl. VAT. Valid until ${fmtDate(q.valid_until, { year: true })}.`}>
              <div className="overflow-hidden rounded-[16px] border border-black/[0.07] bg-white" data-testid="public-quote-commercials">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                      <th className="px-4 py-3 text-left font-semibold @2xl:px-5">Item</th>
                      <th className="whitespace-nowrap px-3 py-3 text-right font-semibold">Qty</th>
                      <th className="hidden whitespace-nowrap px-3 py-3 text-right font-semibold @2xl:table-cell">Unit</th>
                      <th className="whitespace-nowrap px-4 py-3 text-right font-semibold @2xl:px-5">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <React.Fragment key={g.id}>
                        <tr className="border-t border-black/[0.07] bg-[hsl(40_12%_97.5%)]">
                          <td colSpan={3} className="px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-foreground/80 @2xl:px-5">{g.name}</td>
                          <td className="px-4 py-2 text-right font-mono text-[12px] tabular text-muted-foreground @2xl:px-5">{fmtNOK(groupTotal(g))}</td>
                        </tr>
                        {(g.items || []).map((it) => {
                          const u = unitPrice(it);
                          return (
                            <tr key={it.id} className="border-t border-black/[0.05]" data-testid="public-quote-commercial-line">
                              <td className="px-4 py-2.5 @2xl:px-5">
                                <div className="font-medium leading-snug">{it.name}</div>
                                {it.manufacturer && !it.is_service ? <div className="text-[12px] text-muted-foreground">{it.manufacturer}{it.sku ? ` · ${it.sku}` : ""}</div> : null}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono text-[13px] tabular">{it.qty}{it.unit && it.unit !== "pcs" ? ` ${it.unit}` : ""}</td>
                              <td className="hidden whitespace-nowrap px-3 py-2.5 text-right font-mono text-[13px] tabular @2xl:table-cell">{fmtNOK(u)}</td>
                              <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-[13px] tabular font-medium @2xl:px-5">{fmtNOK(u * it.qty)}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 @4xl:grid-cols-[1fr_320px]" data-testid="public-quote-totals">
                <div className="grid grid-cols-3 divide-x divide-black/[0.06] rounded-[16px] border border-black/[0.07] bg-white">
                  {[
                    ["Hardware", t.hardware],
                    ["Software & licences", t.software],
                    ["Services", t.services],
                  ].map(([l, v]) => (
                    <div key={l} className="px-4 py-4 @2xl:px-5">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{l}</div>
                      <div className="mt-2 whitespace-nowrap font-mono text-[15px] tabular font-medium @2xl:text-[17px]">{fmtNOK(v)}</div>
                    </div>
                  ))}
                </div>
                <div className="relative overflow-hidden rounded-[16px] bg-[hsl(var(--ink))] px-5 py-4 text-white print:border print:border-black/20 print:bg-white print:text-foreground">
                  <span className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-[hsl(10_76%_61%/0.45)] blur-3xl print:hidden" />
                  <div className="relative">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60 print:text-muted-foreground">Total excl. VAT</div>
                    <div className="font-display mt-1.5 whitespace-nowrap text-[26px] font-semibold tabular leading-none tracking-[-0.03em] @2xl:text-[30px]" data-testid="public-quote-grand-total">
                      {fmtNOK(t.total_sales)} <span className="text-[13px] font-medium text-white/60 print:text-muted-foreground">NOK</span>
                    </div>
                  </div>
                </div>
              </div>

              {s.optional_items?.length > 0 && (
                <div className="mt-8" data-testid="public-quote-options">
                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Options - not included</div>
                  <div className="flex flex-wrap gap-2">
                    {s.optional_items.map((x, i) => (
                      <span key={i} className="rounded-full border border-black/[0.08] bg-white px-3 py-1.5 text-[13px]">{x}</span>
                    ))}
                  </div>
                </div>
              )}
            </Chapter>

            {/* ------------------------------------------------ timeline */}
            {s.timeline?.length > 0 && (
              <Chapter id="timeline" n={4} title="Timeline" lede={t.max_lead_time_weeks ? `Longest supplier lead time is ${t.max_lead_time_weeks} weeks from order.` : undefined}>
                <ol className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @4xl:grid-cols-4" data-testid="public-quote-timeline">
                  {s.timeline.map((x, i) => (
                    <li key={i} className="relative rounded-[16px] border border-black/[0.07] bg-white p-5">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[11px] tracking-[0.12em] text-primary">{String(i + 1).padStart(2, "0")}</span>
                        {i < s.timeline.length - 1 ? <ArrowRight className="hidden h-3.5 w-3.5 text-black/20 @4xl:block" /> : <Check className="hidden h-3.5 w-3.5 text-black/20 @4xl:block" />}
                      </div>
                      <div className="mt-3 text-[15px] font-medium leading-snug">{x.phase}</div>
                      <div className="mt-1 text-[13px] text-muted-foreground">{x.duration}</div>
                    </li>
                  ))}
                </ol>
                <Scene img={images.timeline} ratio="aspect-[16/9] @2xl:aspect-[21/8]" className="mt-6" testId="public-quote-scene-timeline" />
              </Chapter>
            )}

            {/* ------------------------------------------------ terms */}
            <Chapter id="terms" n={s.timeline?.length ? 5 : 4} title="Assumptions & terms">
              <div className="grid grid-cols-1 gap-8 @3xl:grid-cols-3">
                <div><div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Assumptions</div><Bullets items={s.assumptions} /></div>
                <div><div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Exclusions</div><Bullets items={s.exclusions} /></div>
                <div><div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Terms</div><Bullets items={s.terms} /></div>
              </div>
            </Chapter>

            {/* ------------------------------------------------ acceptance */}
            <Chapter id="acceptance" n={s.timeline?.length ? 6 : 5} title="Acceptance" className="no-print-break" testId="public-quote-section-acceptance">
              <div data-testid="public-quote-acceptance">
                {accepted ? (
                  <div className="flex items-start gap-4 rounded-[16px] border border-[hsl(var(--status-ok)/0.3)] bg-[hsl(var(--status-ok)/0.06)] p-5" data-testid="public-quote-accepted">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--status-ok))] text-white"><Check className="h-4 w-4" /></span>
                    <div>
                      <div className="text-[16px] font-semibold">Proposal accepted</div>
                      <div className="mt-1 text-[14px] leading-6 text-muted-foreground">
                        {q.customer_accepted_by || name ? `By ${q.customer_accepted_by || name}` : ""}
                        {q.customer_accepted_at ? ` on ${fmtDate(q.customer_accepted_at, { year: true })}` : " just now"}. {owner.name} will be in touch shortly to plan delivery.
                      </div>
                    </div>
                  </div>
                ) : done ? (
                  <div className="rounded-[16px] border border-black/[0.07] bg-white p-5 text-[14px]" data-testid="public-quote-response-sent">
                    <div className="font-semibold">Thank you</div>
                    <div className="mt-1 text-muted-foreground">Your message has been passed to {owner.name}.</div>
                  </div>
                ) : (
                  <>
                    <p className="max-w-[640px] text-[15.5px] leading-7">By accepting, {account.name} confirms the scope, commercials and terms in this proposal. Questions and change requests are welcome - we answer within one business day.</p>
                    {interactive ? (<div className="no-print mt-6 flex flex-wrap gap-2">
                      <Button onClick={() => setMode("accept")} className={cn("gap-1.5 rounded-full", mode === "accept" && "ring-2 ring-primary ring-offset-2")} data-testid="public-quote-accept-button"><Check className="h-4 w-4" /> Accept proposal</Button>
                      <Button variant="outline" onClick={() => setMode("changes")} className={cn("gap-1.5 rounded-full", mode === "changes" && "ring-2 ring-primary ring-offset-2")} data-testid="public-quote-changes-button"><PenLine className="h-4 w-4" /> Request changes</Button>
                      <Button variant="outline" onClick={() => setMode("question")} className={cn("gap-1.5 rounded-full", mode === "question" && "ring-2 ring-primary ring-offset-2")} data-testid="public-quote-question-button"><MessageSquare className="h-4 w-4" /> Ask a question</Button>
                    </div>) : null}
                    {interactive && mode && (
                      <div className="no-print mt-5 space-y-3 rounded-[16px] border border-black/[0.07] bg-white p-5 fade-in" data-testid="public-quote-respond-form">
                        <div className="text-[14px] font-semibold">{mode === "accept" ? `Accept on behalf of ${account.name || "your organisation"}` : mode === "changes" ? "What would you like changed?" : "Your question"}</div>
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-11 rounded-[12px]" data-testid="public-quote-respond-name-input" />
                        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={mode === "accept" ? "Optional comment, PO number or preferred start date" : "Describe it in a few sentences"} rows={3} className="rounded-[12px] text-[14px]" data-testid="public-quote-respond-message-input" />
                        <div className="flex gap-2">
                          <Button onClick={respond} disabled={busy || !name.trim() || (mode !== "accept" && !msg.trim())} className="gap-1.5 rounded-full" data-testid="public-quote-respond-submit-button">
                            {busy ? "Sending..." : mode === "accept" ? "Confirm acceptance" : "Send"} <ArrowRight className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" onClick={() => setMode(null)} className="rounded-full">Cancel</Button>
                        </div>
                      </div>
                    )}
                    <div className="mt-10 hidden grid-cols-2 gap-10 print:grid">
                      <div className="border-t border-black/30 pt-2 text-xs text-muted-foreground">Signature, {account.name}</div>
                      <div className="border-t border-black/30 pt-2 text-xs text-muted-foreground">Signature, {entity.name || "Mediability"}</div>
                    </div>
                  </>
                )}

                {/* contact card */}
                <div className="mt-10 flex flex-col gap-5 rounded-[18px] border border-black/[0.07] bg-white p-5 @2xl:flex-row @2xl:items-center @2xl:justify-between @2xl:p-6" data-testid="public-quote-contact-card">
                  <div className="flex items-center gap-4">
                    <UserAvatar name={owner.name} initials={owner.initials} color={owner.color} src={owner.avatar_url} size="xl" className="h-14 w-14" />
                    <div className="leading-tight">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Questions? Talk to</div>
                      <div className="mt-1 text-[16px] font-semibold">{owner.name}</div>
                      <div className="text-[13px] text-muted-foreground">{owner.title || "Mediability"}{owner.region ? ` · ${owner.region}` : ""}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-[13px]">
                    {owner.email ? <a href={`mailto:${owner.email}`} className="inline-flex h-10 items-center gap-2 rounded-full border border-black/[0.08] px-4 hover:border-black/20 focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="public-quote-owner-email"><Mail className="h-3.5 w-3.5" /> {owner.email}</a> : null}
                    {company.phone ? <a href={`tel:${company.phone}`} className="inline-flex h-10 items-center gap-2 rounded-full border border-black/[0.08] px-4 hover:border-black/20 focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="public-quote-owner-phone"><Phone className="h-3.5 w-3.5" /> {company.phone}</a> : null}
                  </div>
                </div>
              </div>
            </Chapter>
          </div>

          {/* ------------------------------------------------ footer */}
          <footer className="mt-20 flex flex-col gap-6 border-t border-black/[0.08] pt-8 text-[12px] text-muted-foreground @2xl:flex-row @2xl:items-end @2xl:justify-between" data-testid="public-quote-footer">
            <div>
              <MediabilityLogo className="h-5" testId="public-quote-logo" />
              <div className="mt-3">{entity.name || company.name}{entity.vat ? ` · VAT ${entity.vat}` : ""}</div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {company.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{company.address}</span>}
                {company.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{company.email}</span>}
                {company.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{company.phone}</span>}
              </div>
            </div>
            <div className="flex items-center gap-4 @2xl:text-right">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Personal link for {account.name}</span>
              <span className="font-mono">{ref}</span>
            </div>
          </footer>
        </article>
  );
}
